import { View, Text, StyleSheet } from "@react-pdf/renderer";
import type { PdfWidgetSnapshot } from "../pdfTypes";
import type { PdfRect } from "../pdfLayout";

// The PDF is a static snapshot of the streamed answer: prose renders as prose,
// a table as a plain grid, and a chart or stat card as a one-line placeholder
// naming what was on screen. Clipped by the tile's height, like the other
// widgets.
//
// ponytail: the grid below is ~20 lines rather than a call into renderTable —
// that renderer draws its own absolutely-positioned bordered cell at the widget
// rect, so it can't be stacked inside this one at an offset.

const s = StyleSheet.create({
  cell: {
    position: "absolute",
    borderWidth: 1,
    borderColor: "#e8e8e8",
    borderRadius: 4,
    backgroundColor: "#fff",
    overflow: "hidden",
  },
  headerTitle: {
    fontWeight: 600,
    color: "#262626",
  },
  partTitle: {
    fontWeight: 500,
    color: "#262626",
  },
  body: {
    color: "#595959",
    lineHeight: 1.6,
  },
  placeholder: {
    color: "#8c8c8c",
    fontStyle: "italic",
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#fafafa",
    borderBottomWidth: 1,
    borderBottomColor: "#e8e8e8",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#f0f0f0",
  },
});

interface TableData {
  headers?: string[];
  rows?: unknown[][];
}

/** True when a table part's data is the flat header/rows shape we can draw. */
function asTable(data: unknown): TableData | null {
  const table = data as TableData | null;
  return Array.isArray(table?.headers) && Array.isArray(table?.rows) ? table : null;
}

export function renderAiInsights(
  snapshot: Extract<PdfWidgetSnapshot, { type: "ai_insights" }>,
  rect: PdfRect,
  scale: number,
) {
  const sidePad = 12 * scale;
  const titleSize = 10;
  const bodySize = Math.max(5, 11 * scale);
  const partTitleSize = Math.max(5, 12 * scale);
  const partGap = Math.max(3, 10 * scale);
  const cellPad = Math.max(1, 3 * scale);

  return (
    <View style={[s.cell, { left: rect.left, top: rect.top, width: rect.width, height: rect.height }]}>
      <Text style={[s.headerTitle, { fontSize: titleSize, position: "absolute", top: 8 * scale, left: sidePad, right: sidePad }]}>
        AI Insights
      </Text>
      <View
        style={{
          position: "absolute",
          top: 37 * scale,
          left: sidePad,
          right: sidePad,
          bottom: 12 * scale,
          overflow: "hidden",
        }}
      >
        <View style={{ position: "absolute", top: 0, left: 0, right: 0 }}>
          {snapshot.parts.map((part, i) => {
            const table = part.type === "table" ? asTable(part.data) : null;
            return (
              <View key={i} style={{ marginBottom: i < snapshot.parts.length - 1 ? partGap : 0 }}>
                {/* A summary section arrives as several parts sharing one title; head the run once. */}
                {part.title && part.title !== snapshot.parts[i - 1]?.title ? (
                  <Text style={[s.partTitle, { fontSize: partTitleSize, marginBottom: 3 * scale }]}>
                    {part.title}
                  </Text>
                ) : null}

                {part.type === "text" ? (
                  <Text style={[s.body, { fontSize: bodySize }]}>{part.content ?? ""}</Text>
                ) : table ? (
                  <View>
                    <View style={s.tableHeaderRow}>
                      {(table.headers ?? []).map((header, hi) => (
                        <Text key={hi} style={{ flex: 1, fontSize: bodySize, fontWeight: 600, padding: cellPad }}>
                          {header}
                        </Text>
                      ))}
                    </View>
                    {(table.rows ?? []).map((row, ri) => (
                      <View key={ri} style={s.tableRow}>
                        {row.map((cell, ci) => (
                          <Text key={ci} style={{ flex: 1, fontSize: bodySize, padding: cellPad }}>
                            {cell != null ? String(cell) : ""}
                          </Text>
                        ))}
                      </View>
                    ))}
                  </View>
                ) : (
                  <Text style={[s.placeholder, { fontSize: bodySize }]}>
                    [{part.type}{part.title ? `: ${part.title}` : ""} — view in the dashboard]
                  </Text>
                )}
              </View>
            );
          })}
        </View>
      </View>
      {snapshot.parts.length === 0 && (
        <View style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, alignItems: "center", justifyContent: "center" }}>
          <Text style={{ fontSize: 8, color: "#8c8c8c" }}>No AI answer</Text>
        </View>
      )}
    </View>
  );
}
